// src/App.jsx
import { BrowserRouter as Router, useLocation } from "react-router-dom";
import { useLoadScript } from "@react-google-maps/api";

import AppRouter from "./router/AppRouter";

import NotificationPopup from "./notifications/NotificationPopup";
import { SocketProvider } from "./socket/SocketContext";
import { NotificationProvider } from "./popup/NotificationContext";
import AlarmProvider from "./hooks/AlarmProvider";
import { useGlobalAlarms } from "./hooks/useGlobalAlarms";
import CenteredAlarmPopup from "./popup/AlarmPopupSystem";
import CommandResponsePopup from "./popup/CommandResponsePopup";

import GlobalQuickModals from "./components/modals/GlobalQuickModals";
import { QuickModalProvider } from "./hooks/QuickModalContext";
import Loader from "./mainframes/Loader";
import Navbar from "./mainframes/Navbar";
import { LanguageProvider } from "./i18n/LanguageProvider";
import CalendarPopupListener from "./mainframes/sidebar/CalendarPopupListener";
import NotificationSetup from "./components/NotificationSetup";
import { registerAxiosCrudSync } from "./utils/registerAxiosCrudSync";
import NavigationMonitor from "./router/NavigationMonitor";
import RouteErrorBoundary from "./router/RouteErrorBoundary";
import { GOOGLE_MAPS_LIBRARIES, GOOGLE_MAPS_VERSION } from "./config/googleMaps";
import WhiteLabelRuntimeProvider from "./modules/whitelabel/runtime/WhiteLabelRuntimeProvider";

registerAxiosCrudSync();

// Helper component to run the background alarm logic
function AlarmMonitor() {
  useGlobalAlarms();
  return null;
}

/**
 * ✅ Global App Layout
 * Navbar yaha mount hoga → har page pe show
 * Agar login page pe hide chahiye to AppLayout me condition laga denge
 */
function AppLayout() {
  const location = useLocation();
  const routeRenderKey = `${location.pathname}${location.search}${location.hash}`;
  const hideNavbar =
    location.pathname === "/" ||
    location.pathname === "/login" ||
    location.pathname === "/signup" ||
    location.pathname === "/live-tracking" ||
    location.pathname === "/live-map" ||
    location.pathname === "/vehicles" ||
    location.pathname.startsWith("/history/") ||
    location.pathname.startsWith("/dashboard") ||
    location.pathname.startsWith("/admin-dashboard") ||
    location.pathname.startsWith("/geofences") ||
    location.pathname === "/notifications" ||
    location.pathname === "/profile" ||
    location.pathname === "/reports" ||
    location.pathname === "/fuel-monitoring" ||
    location.pathname === "/licenses" ||
    location.pathname === "/message" ||
    location.pathname === "/reminders" ||
    location.pathname.startsWith("/routes") ||
    location.pathname.startsWith("/sensors") ||
    location.pathname.startsWith("/expense") ||
    location.pathname.startsWith("/users") ||
    location.pathname.startsWith("/groups") ||
    location.pathname.startsWith("/logistic/") ||
    location.pathname.startsWith("/tasks/");

  return (
    <>
      <NavigationMonitor />
      {!hideNavbar ? <Navbar /> : null}
      <RouteErrorBoundary resetKey={routeRenderKey}>
        <AppRouter />
      </RouteErrorBoundary>
    </>
  );
}

export default function App() {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
    version: GOOGLE_MAPS_VERSION,
  });

  if (loadError) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          fontSize: "18px",
          color: "#ef4444",
        }}
      >
        ❌ Map loading failed. Please refresh the page.
      </div>
    );
  }

  if (!isLoaded) {
    return <Loader fullscreen label="Loading Maps..." background="#f8fafc" />;
  }

  return (
       <LanguageProvider>
      <SocketProvider>
        <NotificationProvider>
          <AlarmProvider>
            <Router>
              <QuickModalProvider>
                <WhiteLabelRuntimeProvider>
                  {/* ✅ Background logic */}
                  <AlarmMonitor />
                  <NotificationSetup />

                  {/* ✅ Global overlays */}
                  <NotificationPopup />
                  <CenteredAlarmPopup />
                  <CommandResponsePopup />
                  <CalendarPopupListener /> 

                  {/* ✅ Global Modals */}
                  <GlobalQuickModals />

                  {/* ✅ Navbar + Routes globally */}
                  <AppLayout />
                </WhiteLabelRuntimeProvider>
              </QuickModalProvider>
            </Router>
          </AlarmProvider>
        </NotificationProvider>
      </SocketProvider>
      </LanguageProvider>
  );
}
